/*----------------------------------
- NEWSLETTER INK BAND
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import type { NewsletterPublicSubscriptionSource } from '@uniquedomains/components/contracts/newsletter/public';

import NewsletterSubscribeForm from '@/client/pages/landing/components/newsletter/NewsletterSubscribeForm';
import { INK_BLOCK_CLASS } from '@/client/pages/landing/components/shared/pageStyles';
import { track } from '@/client/pages/landing/lib/analytics';

/*----------------------------------
- TYPES
----------------------------------*/

type NewsletterInkBandProps = {
    /** The phone action bar scrolls here. */
    anchorId: string;
    /** The source the subscription claims, one per page. */
    source: NewsletterPublicSubscriptionSource;
    heading: string;
    body: string;
    /** A page's own analytics beside the band's, once the list accepted the address. */
    onSubscribed?: () => void;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * The ink capture band closing a public page: one heading, one promise, one field row. The
 * shared form never tracks; the band records the accepted outcome with its source only.
 */
export default function NewsletterInkBand({ anchorId, source, heading, body, onSubscribed }: NewsletterInkBandProps) {
    const titleId = `${anchorId}-title`;

    return (
        <section
            id={anchorId}
            class={`${INK_BLOCK_CLASS} grid scroll-mt-24 gap-4 p-6 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-center lg:gap-10 lg:p-10`}
            aria-labelledby={titleId}
            data-testid={`newsletter-ink-band-${source}`}
        >
            <div>
                <h2 id={titleId} class="text-pretty text-[23px] font-bold leading-[1.15] tracking-[-0.035em] text-white lg:text-[28px]">
                    {heading}
                </h2>
                <p class="mt-2 text-[14.5px] leading-[1.6] text-white/60">{body}</p>
            </div>
            <NewsletterSubscribeForm
                source={source}
                tone="dark"
                size="lg"
                showArrow
                onSubscribed={() => {
                    track('newsletter_subscribe_submitted', { source });
                    onSubscribed?.();
                }}
            />
        </section>
    );
}
